/**
 * Вкладки раздела «LLM-шлюз» и окна над списком: единый список для страницы раздела и для
 * маршрутов /llm-gate/<вкладка> (AppRoutes). Окно маршрута или политики (макет
 * workspace-interactions.js:154-160) открывает адрес над списком своей вкладки:
 * …/new — новая запись, …/:id и …/:id/edit — запись (изменение — у администратора). Лежит
 * отдельным модулем, чтобы роутер не тянул страницу раздела в основной бандл (она грузится
 * лениво).
 */

import { parseEntityId, type EntityId } from '@/lib/entityId';

export const LLM_GATE_TABS = ['routes', 'policies', 'events', 'logs', 'instances'] as const;
export type LlmGateTab = (typeof LLM_GATE_TABS)[number];

/** Вкладка по умолчанию: на неё ведёт адрес /llm-gate и неизвестный сегмент */
export const DEFAULT_LLM_GATE_TAB: LlmGateTab = 'routes';

/** Корень раздела в адресе */
export const LLM_GATE_ROOT = '/llm-gate';

/** Вкладка по адресу: /llm-gate/<вкладка>; неизвестный адрес -- вкладка по умолчанию */
export function llmGateTabFromPath(pathname: string): LlmGateTab {
	const segment = pathname.split('/')[2];
	return LLM_GATE_TABS.find((tab) => tab === segment) ?? DEFAULT_LLM_GATE_TAB;
}

/** Запись, у которой есть окно над списком: маршрут или политика */
export type LlmGateEntity = 'route' | 'policy';

/** Вкладка списка записи */
const ENTITY_TAB: Record<LlmGateEntity, LlmGateTab> = { route: 'routes', policy: 'policies' };

/** Сегмент адреса новой записи и изменения записи */
const NEW_SEGMENT = 'new';
const EDIT_SEGMENT = 'edit';

/**
 * Окно по адресу: new -- новая запись; open -- запись по id (undefined -- в адресе не id, окно
 * говорит «не найдено»). Адрес списка без записи -- окна нет
 */
export type LlmGateDialog =
	| { entity: LlmGateEntity; mode: 'new' }
	| { entity: LlmGateEntity; mode: 'open'; id: EntityId | undefined };

export function llmGateDialogFromPath(pathname: string): LlmGateDialog | null {
	const [, root, tab, record] = pathname.split('/');
	if (`/${root}` !== LLM_GATE_ROOT || record === undefined || record === '') return null;
	const entity = (Object.keys(ENTITY_TAB) as LlmGateEntity[]).find(
		(candidate) => ENTITY_TAB[candidate] === tab,
	);
	if (!entity) return null;
	return record === NEW_SEGMENT
		? { entity, mode: 'new' }
		: { entity, mode: 'open', id: parseEntityId(record) };
}

/** Адрес вкладки записи -- куда возвращает закрытие окна */
export function llmGateListPath(entity: LlmGateEntity): string {
	return `${LLM_GATE_ROOT}/${ENTITY_TAB[entity]}`;
}

/** Адрес окна новой записи */
export function llmGateNewPath(entity: LlmGateEntity): string {
	return `${llmGateListPath(entity)}/${NEW_SEGMENT}`;
}

/** Адрес окна записи: просмотр или изменение (адрес изменения закрыт AdminRoute) */
export function llmGateRecordPath(entity: LlmGateEntity, id: EntityId, edit = false): string {
	/* Учебный id (train:…) состоит из символов, допустимых в сегменте пути: кодировать нечего */
	const path = `${llmGateListPath(entity)}/${id}`;
	return edit ? `${path}/${EDIT_SEGMENT}` : path;
}
